import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GALAXY_UPLOADER_SERVICE_TOKEN, GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SmsDocumentUploaderService } from './sms-document-uploader.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { SMSI18NModule } from '../../../../assets/i18n/sms-i18n.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const PROOF_DOCUMENT_CONTROL = 'proofDocument';
export const DOCUMENT_TYPE_CONTROL = 'documentType';
export const DOCUMENT_ID_CONTROL = 'documentID';

const NUMBER_OF_MEGABYES = 5;
const FILE_SIZE_LIMIT = NUMBER_OF_MEGABYES * 1024 * 1024;
const ACCEPTABLE_FILE_TYPES = '.pdf,.png,.jpg,.jpeg';

export interface SmsDocumentOption {
  label: string;
  value: string;
}

@Component({
  selector: 'sms-document-uploader',
  imports: [CommonModule, GalaxyUploaderModule, GalaxyFormFieldModule, MatInput, ReactiveFormsModule, TranslateModule, SMSI18NModule],
  templateUrl: './sms-document-uploader.component.html',
  styleUrl: './sms-document-uploader.component.scss',
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: SmsDocumentUploaderService }],
})
export class SmsDocumentUploaderComponent implements OnInit, OnChanges {
  @Input({ required: true }) parentFormGroup!: FormGroup;
  @Input({ required: true }) options: SmsDocumentOption[] = [];

  @Input() fieldTitle = '';
  @Input() fieldHint = '';
  @Input() disabled = false;

  protected readonly fileSizeLimit = FILE_SIZE_LIMIT;
  protected readonly acceptableFileTypes = ACCEPTABLE_FILE_TYPES;

  formGroup = new FormGroup({
    documentType: new FormControl('', Validators.required),
    documentID: new FormControl('', Validators.required),
  });

  constructor(@Inject(GALAXY_UPLOADER_SERVICE_TOKEN) private uploadService: SmsDocumentUploaderService) {
    this.uploadService.fileID$.pipe(takeUntilDestroyed()).subscribe((fileID) => {
      this.formGroup.patchValue({ documentID: fileID });
    });
  }

  ngOnInit() {
    if (this.options.length >= 1) {
      this.formGroup.patchValue({ documentType: this.options[0].value });
    }
    this.parentFormGroup.addControl('proofDocument', this.formGroup);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] && !changes['options'].firstChange) {
      this.resetSelectedDocumentType();
    }
  }

  private resetSelectedDocumentType() {
    const selectedType = this.formGroup.controls.documentType.value;
    const selectionStillOffered = this.options.some((option) => option.value === selectedType);
    if (!selectionStillOffered) {
      this.formGroup.patchValue({ documentType: this.options[0]?.value ?? '' });
    }
  }

  protected readonly NUMBER_OF_MEGABYES = NUMBER_OF_MEGABYES;
}
